import type { VaultData } from '../domain/models';

export interface VaultEnvelope {
  format: 'coverage-timeline-vault';
  version: 1 | 2;
  kdf: { name: 'PBKDF2' | 'device-key'; hash?: 'SHA-256'; iterations?: 210000; salt?: string };
  cipher: { name: 'AES-GCM'; iv: string; data: string };
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function asArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

let sessionKey: { passphrase: string; salt: string; key: CryptoKey } | undefined;

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  if (passphrase.length < 10) throw new Error('PASSPHRASE_TOO_SHORT');
  const saltKey = bytesToBase64(salt);
  if (sessionKey && sessionKey.passphrase === passphrase && sessionKey.salt === saltKey) return sessionKey.key;
  const material = await crypto.subtle.importKey('raw', encoder.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: asArrayBuffer(salt), iterations: 210000 }, material,
    { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
  );
  sessionKey = { passphrase, salt: saltKey, key };
  return key;
}

export function clearSessionKey(): void {
  sessionKey = undefined;
}

export async function encryptVault(data: VaultData, passphrase: string, existingSalt?: string, deviceKey?: CryptoKey): Promise<VaultEnvelope> {
  const salt = existingSalt ? base64ToBytes(existingSalt) : crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = deviceKey ?? await deriveKey(passphrase, salt);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: asArrayBuffer(iv) }, key, encoder.encode(JSON.stringify(data)));
  return {
    format: 'coverage-timeline-vault', version: deviceKey ? 2 : 1,
    kdf: deviceKey ? {name:'device-key'} : { name: 'PBKDF2', hash: 'SHA-256', iterations: 210000, salt: bytesToBase64(salt) },
    cipher: { name: 'AES-GCM', iv: bytesToBase64(iv), data: bytesToBase64(new Uint8Array(ciphertext)) },
  };
}

export async function decryptVault(envelope: VaultEnvelope, passphrase: string, deviceKey?: CryptoKey): Promise<VaultData> {
  if (envelope.format !== 'coverage-timeline-vault' || !((envelope.version === 1 && envelope.kdf.name === 'PBKDF2' && envelope.kdf.iterations === 210000 && envelope.kdf.salt) || (envelope.version === 2 && envelope.kdf.name === 'device-key' && deviceKey))) {
    throw new Error('ARCHIVE_FORMAT_UNSUPPORTED');
  }
  const key = envelope.version === 2 ? deviceKey! : await deriveKey(passphrase, base64ToBytes(envelope.kdf.salt!));
  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: asArrayBuffer(base64ToBytes(envelope.cipher.iv)) }, key, asArrayBuffer(base64ToBytes(envelope.cipher.data)),
    );
    const parsed = JSON.parse(decoder.decode(plaintext)) as VaultData;
    if (parsed.schemaVersion !== 1 || !Array.isArray(parsed.cases) || !Array.isArray(parsed.history)) throw new Error('ARCHIVE_SCHEMA_INVALID');
    return { ...parsed, verificationQueue: parsed.verificationQueue ?? [], rechecks: parsed.rechecks ?? [] };
  } catch (error) {
    if (error instanceof Error && error.message === 'ARCHIVE_SCHEMA_INVALID') throw error;
    throw new Error('VAULT_UNLOCK_FAILED');
  }
}
